/*
|--------------------------------------------------------------------------
| Component: SelectBox.jsx
| Author: Sayed Ahmed
| Description: A reusable select dropdown component that supports both
|              controlled and react-hook-form usage, dynamic options,
|              loading state, validation, and localization.
|--------------------------------------------------------------------------
*/

import React from "react";
import { useTranslation } from "react-i18next";

export default function SelectBox({
  label,                              // Label text
  name = "select",                    // Field name
  id = "select",                      // HTML id
  register = null,                    // react-hook-form register function
  required = false,                   // Whether the field is required
  value,                              // Controlled value
  onChange,                           // Controlled onChange handler
  loading,                            // Show loading state
  data = [],                          // Dropdown options
  errors,                             // react-hook-form errors
  disabled,                           // Disable select input
  placeholder = "",                   // Placeholder text
  className = "form-select form-select-lg mx-1", // Custom classes
  getOptionLabel = (item) => item.name, // Extract label from item
  getOptionValue = (item) => item.id,   // Extract value from item
  showLabel = false,                 // Show label above select
}) {
  const { t } = useTranslation();

  // Dynamic props based on whether `register` is passed
  const selectProps = {
    name,
    id,
    className,
    disabled: disabled || loading || data.length === 0,
    ...(register
      ? register(name, {
          required: required ? t("Field is required") : false,
        })
      : { value, onChange }),
  };

  return (
    <div className="form-group">
      {/* Render label if required */}
      {showLabel && label && (
        <label htmlFor={id} className="mb-2">
          {label}
        </label>
      )}

      {/* Select Input */}
      <select {...selectProps}>
        <option value="" disabled>{placeholder || t("Select an option")}</option>

        {/* Loading or fallback */}
        {loading ? (
          <option>{placeholder || t("Loading...")}</option>
        ) : data.length === 0 ? (
          <option>{placeholder || t("No data available")}</option>
        ) : (
          data.map((item) => (
            <option key={getOptionValue(item)} value={getOptionValue(item)}>
              {getOptionLabel(item)}
            </option>
          ))
        )}
      </select>

      {/* Validation Error */}
      {errors?.[name]?.message && (
        <p className="text-red-600 text-sm">{t(errors[name]?.message)}</p>
      )}
    </div>
  );
}
